import authService from '../services/authService';
import { getGraphClient } from '../services/graphClient';
import graphService from '../services/graphService';

/**
 * Script de diagnostic complet pour OneDrive
 * Teste toute la chaîne d'authentification et d'accès aux données
 */
export const runOneDriveHealthCheck = async () => {
  const results = {
    timestamp: new Date().toISOString(),
    platform: navigator.userAgent.includes('iPhone') || navigator.userAgent.includes('iPad') ? 'iOS' : 'Desktop',
    tests: [],
    summary: {
      total: 0,
      passed: 0,
      failed: 0,
      warnings: 0
    }
  };

  // Helper pour ajouter un résultat de test
  const addTestResult = (name, success, data = {}, warning = false) => {
    results.tests.push({
      name,
      success,
      warning,
      timestamp: new Date().toISOString(),
      ...data
    });
    
    results.summary.total++;
    if (success && !warning) {
      results.summary.passed++;
    } else if (warning) {
      results.summary.warnings++;
    } else {
      results.summary.failed++;
    }
  };

  // Test 1: Token Azure valide
  try {
    const token = await authService.getValidAzureToken();
    if (token) {
      // Décoder le token pour obtenir des informations
      const tokenParts = token.split('.');
      if (tokenParts.length === 3) {
        try {
          const payload = JSON.parse(atob(tokenParts[1]));
          const now = Date.now() / 1000;
          const timeToExpiry = payload.exp - now;
          
          addTestResult('Token Azure valide', true, {
            hasToken: true,
            tokenPrefix: token.substring(0, 20) + '...',
            expiresIn: Math.round(timeToExpiry / 60) + ' minutes',
            audience: payload.aud,
            scopes: payload.scp || 'Non disponible'
          }, timeToExpiry < 300); // Warning si expire dans moins de 5 min
        } catch (decodeError) {
          addTestResult('Token Azure valide', true, {
            hasToken: true,
            tokenPrefix: token.substring(0, 20) + '...',
            decodeError: 'Impossible de décoder le JWT'
          }, true);
        }
      } else {
        addTestResult('Token Azure valide', false, {
          error: 'Format de token invalide'
        });
      }
    } else {
      addTestResult('Token Azure valide', false, {
        error: 'Aucun token disponible'
      });
    }
  } catch (error) {
    addTestResult('Token Azure valide', false, {
      error: error.message,
      stack: error.stack?.split('\n').slice(0, 3) // Premières lignes de la stack
    });
  }

  // Test 2: Client Graph initialisé
  try {
    const client = await getGraphClient();
    addTestResult('Client Graph initialisé', !!client, {
      clientType: client?.constructor?.name || 'Unknown',
      hasAuthProvider: !!client?.api
    });
  } catch (error) {
    addTestResult('Client Graph initialisé', false, {
      error: error.message
    });
  }

  // Test 3: Accès OneDrive basique
  try {
    const result = await graphService.checkOneDriveAccess();
    addTestResult('Accès OneDrive basique', result.success, {
      driveInfo: result.success ? result.driveInfo : null,
      needsReconnection: result.needsReconnection,
      statusCode: result.statusCode,
      error: result.error
    }, result.needsReconnection);
  } catch (error) {
    addTestResult('Accès OneDrive basique', false, {
      error: error.message,
      statusCode: error.statusCode || 'N/A'
    });
  }

  // Test 4: Liste des fichiers OneDrive
  try {
    const files = await graphService.listRootFiles();
    addTestResult('Liste fichiers OneDrive', Array.isArray(files), {
      fileCount: Array.isArray(files) ? files.length : 0,
      firstFiles: Array.isArray(files) ? files.slice(0, 3).map(f => ({
        name: f.name,
        size: f.size,
        type: f.folder ? 'folder' : 'file'
      })) : []
    });
  } catch (error) {
    addTestResult('Liste fichiers OneDrive', false, {
      error: error.message,
      statusCode: error.statusCode || 'N/A'
    });
  }

  // Test 5: Vérification des scopes requis
  try {
    const token = await authService.getValidAzureToken();
    if (token) {
      const tokenParts = token.split('.');
      if (tokenParts.length === 3) {
        const payload = JSON.parse(atob(tokenParts[1]));
        const scopes = payload.scp ? payload.scp.split(' ') : [];
        
        // Scopes essentiels pour OneDrive
        const essentialScopes = ['User.Read'];
        const oneDriveScopes = ['Files.Read.All', 'Files.ReadWrite.All', 'Files.Read', 'Files.ReadWrite'];
        const sharepointScopes = ['Sites.Read.All', 'Sites.ReadWrite.All'];
        
        // Vérifier les scopes essentiels
        const missingEssential = essentialScopes.filter(scope => !scopes.includes(scope));
        
        // Vérifier qu'au moins un scope OneDrive est présent
        const hasOneDriveAccess = oneDriveScopes.some(scope => scopes.includes(scope));
        
        // SharePoint est optionnel
        const hasSharePointAccess = sharepointScopes.some(scope => scopes.includes(scope));
        
        const allGood = missingEssential.length === 0 && hasOneDriveAccess;
        
        addTestResult('Scopes OneDrive requis', allGood, {
          availableScopes: scopes,
          essentialScopes: essentialScopes,
          missingEssential: missingEssential,
          hasOneDriveAccess: hasOneDriveAccess,
          hasSharePointAccess: hasSharePointAccess,
          oneDriveScopesFound: oneDriveScopes.filter(scope => scopes.includes(scope)),
          sharepointScopesFound: sharepointScopes.filter(scope => scopes.includes(scope)),
          analysis: allGood ? 
            'Parfait ! OneDrive est entièrement fonctionnel' + (hasSharePointAccess ? ' + SharePoint disponible' : '') :
            `Problème détecté: ${missingEssential.length > 0 ? 'Scopes essentiels manquants' : ''}${!hasOneDriveAccess ? 'Pas d\'accès OneDrive' : ''}`
        }, !allGood || (!hasSharePointAccess && sharepointScopes.length > 0));
      }
    }
  } catch (error) {
    addTestResult('Scopes OneDrive requis', false, {
      error: error.message
    });
  }

  // Test 6: Performance de l'API
  const startTime = Date.now();
  try {
    await graphService.listRootFiles();
    const responseTime = Date.now() - startTime;
    
    addTestResult('Performance API OneDrive', responseTime < 5000, {
      responseTime: responseTime + 'ms',
      performance: responseTime < 1000 ? 'Excellente' : 
                  responseTime < 3000 ? 'Bonne' : 
                  responseTime < 5000 ? 'Acceptable' : 'Lente'
    }, responseTime > 3000);
  } catch (error) {
    addTestResult('Performance API OneDrive', false, {
      error: error.message,
      responseTime: (Date.now() - startTime) + 'ms'
    });
  }

  // Générer le rapport final
  results.healthScore = Math.round((results.summary.passed / results.summary.total) * 100);
  results.status = results.healthScore >= 80 ? 'Excellent' :
                  results.healthScore >= 60 ? 'Bon' :
                  results.healthScore >= 40 ? 'Moyen' : 'Critique';

  return results;
};

/**
 * Exécute les tests et émet des notifications appropriées
 */
export const runOneDriveHealthCheckWithNotifications = async () => {
  try {
    const results = await runOneDriveHealthCheck();
    
    // Émettre des notifications basées sur les résultats
    const failedTests = results.tests.filter(t => !t.success);
    const warningTests = results.tests.filter(t => t.warning);
    
    // Log système au lieu de notifications utilisateur
    try {
      const systemLogService = (await import('../services/systemLogService.js')).default;
      const currentUser = await authService.getCurrentUser();
      
      if (failedTests.length === 0 && warningTests.length === 0) {
        // Tout va bien
        await systemLogService.logOneDrive(
          currentUser?.uid || 'unknown',
          currentUser?.email || 'unknown',
          currentUser?.displayName || currentUser?.email?.split('@')[0] || 'Utilisateur',
          `OneDrive fonctionne parfaitement (${results.summary.passed}/${results.summary.total} tests)`,
          'success',
          { 
            testResults: results.summary,
            testsDetails: results.details
          }
        );
      } else if (failedTests.length === 0) {
        // Seulement des warnings
        await systemLogService.logOneDrive(
          currentUser?.uid || 'unknown',
          currentUser?.email || 'unknown',
          currentUser?.displayName || currentUser?.email?.split('@')[0] || 'Utilisateur',
          `OneDrive fonctionne avec ${warningTests.length} avertissement(s)`,
          'warning',
          { 
            testResults: results.summary,
            warningTests: warningTests,
            testsDetails: results.details
          }
        );
      } else {
        // Il y a des échecs
        await systemLogService.logOneDrive(
          currentUser?.uid || 'unknown',
          currentUser?.email || 'unknown',
          currentUser?.displayName || currentUser?.email?.split('@')[0] || 'Utilisateur',
          `OneDrive a ${failedTests.length} erreur(s) critique(s)`,
          'error',
          { 
            testResults: results.summary,
            failedTests: failedTests,
            warningTests: warningTests,
            testsDetails: results.details
          }
        );
      }
    } catch (logError) {
      console.warn('[OneDriveHealth] Impossible de logger les résultats du test OneDrive:', logError);
    }
    
    // Ancienne notification maintenant supprimée - voir logs système pour les détails
    /*
    } else {
      // Il y a des échecs
      window.dispatchEvent(new CustomEvent('auth-notification', {
        detail: {
          message: `❌ ${failedTests.length} test(s) OneDrive échoué(s). Vérifiez la connexion.`,
          type: 'error',
          duration: 10000
        }
      }));
    }
    */
    
    return results;
  } catch (error) {
    // Log système au lieu de notification utilisateur
    try {
      const systemLogService = (await import('../services/systemLogService.js')).default;
      const currentUser = await authService.getCurrentUser();
      await systemLogService.logError(
        currentUser?.uid || 'unknown',
        currentUser?.email || 'unknown',
        currentUser?.displayName || currentUser?.email?.split('@')[0] || 'Utilisateur',
        `Erreur lors du diagnostic OneDrive: ${error.message}`,
        error,
        { 
          testType: 'onedrive_health',
          errorDuringTest: true
        }
      );
    } catch (logError) {
      console.warn('[OneDriveHealth] Impossible de logger l\'erreur du diagnostic OneDrive:', logError);
    }
    
    throw error;
  }
};

/**
 * Version simplifiée pour un diagnostic rapide
 */
export const quickOneDriveCheck = async () => {
  try {
    const result = await graphService.checkOneDriveAccess();
    return {
      healthy: result.success,
      needsReconnection: result.needsReconnection,
      message: result.success ? 'OneDrive accessible' : 'OneDrive inaccessible'
    };
  } catch (error) {
    return {
      healthy: false,
      needsReconnection: true,
      message: 'Erreur de connexion OneDrive'
    };
  }
};